import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { AnchorHTMLAttributes, CSSProperties, HTMLAttributes, MouseEvent, ReactNode } from 'react'
import { cn } from './cn'
import { IconButton } from './IconButton'
import { IconMenu, IconClose } from './Icons'

interface ShellContext { open: boolean; toggle: () => void; close: () => void }
const Ctx = createContext<ShellContext | null>(null)
export function useAppShell(): ShellContext | null {
  return useContext(Ctx)
}

export interface AppShellProps {
  topbar?: ReactNode
  sidebar?: ReactNode
  railWidth?: number
  children?: ReactNode
  className?: string
}
/**
 * App shell — fixed topbar + collapsible side rail + a single managed scroll
 * region. Slot-based; manages the mobile drawer.
 *
 * Layout invariants (CRM-LOWMED-011 — fixes the sidebar/main double-scroll +
 * "lower nav cut off"):
 *  - The shell stays FIXED to the viewport: `min-h-[100dvh]` (never shorter than
 *    the dynamic viewport, so it can't collapse) AND `lg:h-[100dvh]` +
 *    `lg:overflow-hidden` on desktop so the shell itself never scrolls.
 *  - `<main>` is the PRIMARY managed scroll container (the CONTENT scrolls).
 *  - The side rail (`<nav>`) gets its OWN bounded height so when the nav list is
 *    taller than the viewport it scrolls INTERNALLY with bottom padding — the
 *    lower nav (e.g. Governance/Reports) is always fully reachable, never clipped
 *    behind the viewport edge. `overscroll-contain` stops its scroll bleeding
 *    into the page, so the two regions never fight as one "double scrollbar".
 *  - The mobile drawer + scrim sit at `z-overlay` (500) — strictly BELOW Modal
 *    (`z-modal`, 1000) and Toasts (`z-toast`, 2000).
 *  - Body scroll-lock while the mobile drawer is open.
 */
export function AppShell({ topbar, sidebar, railWidth = 264, children, className = '' }: AppShellProps) {
  const [open, setOpen] = useState(false)
  const toggle = useCallback(() => setOpen((o) => !o), [])
  const close = useCallback(() => setOpen(false), [])
  const ctx = useMemo<ShellContext>(() => ({ open, toggle, close }), [open, toggle, close])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open, close])
  const style = { '--rail': `${railWidth}px` } as CSSProperties
  return (
    <Ctx.Provider value={ctx}>
      <div
        data-app-shell
        className={cn(
          'min-h-[100dvh] lg:h-[100dvh] lg:overflow-hidden bg-whisper grid grid-rows-[auto_minmax(0,1fr)] lg:grid-cols-[var(--rail)_minmax(0,1fr)]',
          className,
        )}
        style={style}
      >
        <div className="lg:col-span-2">{topbar}</div>
        {open && <button aria-label="Close navigation" onClick={ctx.close} className="fixed inset-0 z-overlay bg-ink-deep/55 lg:hidden" />}
        {sidebar}
        <main
          id="main"
          tabIndex={-1}
          className="min-w-0 min-h-0 overflow-y-auto overscroll-contain lg:col-start-2 px-4 sm:px-6 lg:px-7 py-6 pb-12 focus:outline-none"
        >
          {children}
        </main>
      </div>
    </Ctx.Provider>
  )
}

export interface TopbarProps extends HTMLAttributes<HTMLElement> {
  brand?: ReactNode
  search?: ReactNode
  actions?: ReactNode
}
export function Topbar({ brand, search, actions, className = '', ...props }: TopbarProps) {
  const shell = useAppShell()
  return (
    <header className={cn('sticky top-0 z-sticky flex items-center gap-4 px-5 py-3 bg-ink text-on-ink border-b border-veil', className)} {...props}>
      <IconButton tone="ink" className="lg:hidden" aria-label={shell?.open ? 'Close navigation' : 'Open navigation'} aria-expanded={!!shell?.open} onClick={shell?.toggle}>
        {shell?.open ? <IconClose /> : <IconMenu />}
      </IconButton>
      {brand}
      {search}
      <div className="flex-1" aria-hidden="true" />
      <div className="flex items-center gap-2">{actions}</div>
    </header>
  )
}

export interface SidebarProps extends HTMLAttributes<HTMLElement> {
  ariaLabel?: string
}
export function Sidebar({ ariaLabel = 'Primary', className = '', children, ...props }: SidebarProps) {
  const shell = useAppShell()
  return (
    <nav
      data-app-sidebar
      aria-label={ariaLabel}
      tabIndex={0}
      className={cn(
        // CRM-LOWMED-011: `overscroll-contain` + a bottom pad (`pb-6`) so when the
        // nav list overflows it scrolls cleanly INSIDE itself and the lowest items
        // (Governance/Reports) are fully reachable, never clipped at the edge.
        'flex flex-col gap-2 overflow-y-auto overscroll-contain bg-ink-deep text-on-ink-soft p-3 pb-6 fixed inset-y-0 left-0 z-overlay w-[280px] shadow-3 transition-transform duration-base motion-reduce:transition-none focus-visible:outline-none focus-visible:shadow-focus-dark',
        shell?.open ? 'translate-x-0' : '-translate-x-full',
        // On desktop the rail lives in the grid row and is bounded to that track
        // (lg:h-full lg:min-h-0) so its OWN scroll handles a tall nav while
        // `<main>` owns the content scroll — no shared/competing double scrollbar.
        'lg:static lg:z-auto lg:w-auto lg:h-full lg:min-h-0 lg:self-stretch lg:shadow-none lg:translate-x-0 lg:border-r lg:border-veil-soft',
        className
      )}
      {...props}
    >
      {children}
    </nav>
  )
}

export function NavSection({
  className = '',
  children,
  'aria-label': ariaLabelProp,
  ...props
}: HTMLAttributes<HTMLParagraphElement>) {
  const titleLabel = typeof children === 'string' ? children.trim() : undefined
  const accessibleName = ariaLabelProp ?? titleLabel
  return (
    <p
      role={accessibleName ? 'group' : undefined}
      aria-label={accessibleName}
      className={cn('mt-4 px-3 text-micro font-semibold uppercase tracking-eyebrow text-on-ink-tertiary', className)}
      {...props}
    >
      {children}
    </p>
  )
}

export interface NavItemProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  icon?: ReactNode
  active?: boolean
  badge?: ReactNode
}
export function NavItem({ href = '#', icon, active = false, badge, onClick, className = '', children, ...props }: NavItemProps) {
  const shell = useAppShell()
  return (
    <a
      href={href}
      aria-current={active ? 'page' : undefined}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => { onClick?.(e); shell?.close() }}
      className={cn(
        'flex items-center gap-3 px-3 rounded-control min-h-tap text-sm no-underline transition-colors duration-fast ease-out-soft focus-visible:outline-none focus-visible:shadow-focus-dark',
        active ? 'bg-veil text-on-ink [box-shadow:inset_3px_0_0_theme(colors.mustard.DEFAULT)]' : 'text-on-ink-soft hover:bg-veil-soft hover:text-on-ink',
        className
      )}
      {...props}
    >
      {icon && <span className="w-5 h-5 shrink-0" aria-hidden="true">{icon}</span>}
      <span className="truncate">{children}</span>
      {badge != null && <span className="ml-auto text-micro font-semibold bg-mustard text-ink rounded-pill px-1.5 min-w-[18px] text-center">{badge}</span>}
    </a>
  )
}
